<template>
  <div class="search-box" :class="{ 'search-box--focused': isFocused }" ref="searchBoxRef">
    <!-- Row 1: Input + clear -->
    <div class="search-row-1 d-flex align-center">
      <textarea
        ref="inputRef"
        v-model="searchString"
        class="search-input flex-grow-1"
        :placeholder="placeholder"
        aria-label="Search scholarly works"
        :autofocus="autofocus"
        rows="1"
        @keydown.enter.prevent="onEnter"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.escape="onEscape"
        @input="onTextareaInput"
        @paste="onTextareaPaste"
        @focus="onFocus"
        @blur="onBlur"
        autocomplete="off"
      />
      <v-btn
        v-if="searchString"
        icon
        variant="text"
        size="small"
        class="mr-2"
        aria-label="Clear search"
        @click="clearSearch"
      >
        <v-icon size="16">mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Autocomplete dropdown -->
    <div v-if="showDropdown" class="autocomplete-dropdown">
      <div class="autocomplete-list">
        <div
          v-for="(item, index) in suggestions"
          :key="item.id + '-' + item._acType"
          class="autocomplete-item d-flex align-center"
          :class="{ 'autocomplete-item--highlighted': index === highlightedIndex }"
          @mousedown.prevent="selectSuggestion(item)"
          @mouseenter="highlightedIndex = index"
        >
          <v-icon size="16" class="autocomplete-item-icon">{{ item._icon }}</v-icon>
          <div class="autocomplete-item-name">{{ item.display_name }}</div>
        </div>
      </div>
    </div>

    <!-- Divider -->
    <div v-if="showRow2" class="search-divider"></div>

    <!-- Row 2: Entity selector (left) | Mode toggle | spacer | Options menu -->
    <div v-if="showRow2" class="search-row-2 d-flex align-center">
      <entity-selector-button @entity-selected="focusSearchInput" />

      <!-- Search mode toggle (works only) -->
      <div v-if="isWorksEntity" class="mode-toggle ml-2">
        <button
          v-for="mode in availableModes"
          :key="mode.value"
          type="button"
          class="mode-toggle-btn"
          :class="{ 'mode-toggle-btn--active': searchMode === mode.value }"
          @click="setMode(mode.value)"
        >
          <v-icon size="14" class="mode-toggle-icon">{{ mode.icon }}</v-icon>
          <span class="mode-toggle-label">{{ mode.label }}</span>
        </button>
      </div>

      <v-spacer />

      <!-- Options menu (fields + stemming) hidden for now - simpler UX. -->
      <template v-if="false && isWorksEntity && searchMode === 'term'">
        <v-menu v-model="optionsMenuOpen" location="bottom end" :close-on-content-click="false">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              icon
              variant="text"
              size="small"
              class="control-btn"
              aria-label="Search options"
            >
              <v-icon size="18">mdi-tune-variant</v-icon>
            </v-btn>
          </template>
          <v-list density="compact" min-width="280">
            <v-list-subheader>Search fields</v-list-subheader>

            <v-list-item @click="setField('title'); optionsMenuOpen = false">
              <v-list-item-title>Title</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'title'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-list-item @click="setField('title_and_abstract'); optionsMenuOpen = false">
              <v-list-item-title>Title & abstract</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'title_and_abstract'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-list-item @click="setField('all'); optionsMenuOpen = false">
              <v-list-item-title>Title, abstract, & fulltext</v-list-item-title>
              <template #append>
                <v-icon v-if="searchField === 'all'" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-divider class="my-1" />

            <v-list-subheader>Stemming</v-list-subheader>

            <v-list-item @click="disableStemming(false)">
              <v-list-item-title>Enable stemming</v-list-item-title>
              <v-list-item-subtitle class="menu-subtitle">looking = look, looker, etc</v-list-item-subtitle>
              <template #append>
                <v-icon v-if="!stemmingDisabled" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>

            <v-list-item @click="disableStemming(true)">
              <v-list-item-title>Disable stemming</v-list-item-title>
              <template #append>
                <v-icon v-if="stemmingDisabled" class="check-icon">mdi-check</v-icon>
              </template>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <span v-else-if="isWorksEntity && searchMode === 'ai'" class="mode-hint">
        Ask a question. Get an answer with citations.
      </span>
      <span v-else-if="isWorksEntity && searchMode === 'semantic'" class="mode-hint">
        Vendor papers, case studies, textbooks, analyst reports.
      </span>
    </div>
  </div>
</template>

<script setup>
defineOptions({ name: 'SearchBox' });

import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useDisplay } from 'vuetify';
import { debounce } from 'lodash';
import { getEntityConfig, getEntityConfigs } from '@/entityConfigs';
import { api } from '@/api';
import { createSimpleFilter, filtersFromUrlStr, filtersAsUrlStr } from '@/filterConfigs';
import { url } from '@/url';
import { facetConfigs } from '@/facetConfigs';
import { extractIssn } from '@/components/searchBox.helpers';
import EntitySelectorButton from '@/components/EntitySelectorButton.vue';

const props = defineProps({
  autofocus: Boolean,
  showExamples: Boolean,
});

// --- Identifier extraction helpers ---

function extractDoi(str) {
  if (!str) return null;
  const trimmed = str.trim();

  // URL form: https://doi.org/10.xxx or https://dx.doi.org/10.xxx (http too)
  const urlMatch = trimmed.match(/^https?:\/\/(?:dx\.)?doi\.org\/(10\..+)$/i);
  if (urlMatch) return `https://doi.org/${urlMatch[1]}`;

  // Labeled form: "DOI: 10.xxx" or "doi:10.xxx"
  const labelMatch = trimmed.match(/^doi:\s*(10\..+)$/i);
  if (labelMatch) return `https://doi.org/${labelMatch[1]}`;

  // Bare DOI: starts with 10. and contains a slash
  if (/^10\.\d{4,}/.test(trimmed) && trimmed.includes('/')) {
    return `https://doi.org/${trimmed}`;
  }

  return null;
}

function extractOrcid(str) {
  if (!str) return null;
  const trimmed = str.trim();

  // URL form: https://orcid.org/0000-...
  const urlMatch = trimmed.match(/^https?:\/\/orcid\.org\/(\d{4}-\d{4}-\d{4}-\d{3}[\dX])$/i);
  if (urlMatch) return `https://orcid.org/${urlMatch[1].toUpperCase()}`;

  // Labeled form: "ORCID: 0000-..." or "orcid:0000-..."
  const labelMatch = trimmed.match(/^orcid:\s*(\d{4}-\d{4}-\d{4}-\d{3}[\dX])$/i);
  if (labelMatch) return `https://orcid.org/${labelMatch[1].toUpperCase()}`;

  // Bare ORCID: exactly the 4-4-4-4 pattern
  const bareMatch = trimmed.match(/^(\d{4}-\d{4}-\d{4}-\d{3}[\dX])$/i);
  if (bareMatch) return `https://orcid.org/${bareMatch[1].toUpperCase()}`;

  return null;
}

async function tryIdentifierLookup() {
  const input = searchString.value;

  const doi = extractDoi(input);
  if (doi) {
    try {
      const resp = await api.get('/works', { filter: `doi:${doi}`, per_page: 1, select: 'id' });
      if (resp.results && resp.results.length > 0) {
        const entityId = resp.results[0].id?.replace('https://openalex.org/', '') || resp.results[0].id;
        searchString.value = '';
        dismissDropdown();
        router.push({ name: 'EntityPage', params: { entityType: 'works', entityId } });
        return true;
      }
    } catch (e) {
      // DOI not found or API error — fall through to regular search
    }
    return false;
  }

  const orcid = extractOrcid(input);
  if (orcid) {
    try {
      const resp = await api.get('/authors', { filter: `orcid:${orcid}`, per_page: 1, select: 'id' });
      if (resp.results && resp.results.length > 0) {
        searchString.value = '';
        dismissDropdown();
        const currentFilters = filtersFromUrlStr('works', route.query.filter);
        const newFilter = createSimpleFilter('works', 'authorships.author.id', resp.results[0].id);
        url.pushNewFilters([...currentFilters, newFilter], 'works');
        return true;
      }
    } catch (e) {
      // ORCID not found or API error — fall through to regular search
    }
    return false;
  }

  return false;
}

const store = useStore();
const route = useRoute();
const router = useRouter();
const { smAndDown } = useDisplay();

const inputRef = ref(null);
const searchBoxRef = ref(null);
const searchString = ref('');
const searchMode = ref('term'); // 'term' (keyword), 'ai' (chat), or 'semantic' (scr content store)
const searchField = ref('title_and_abstract'); // 'all' | 'title' | 'title_and_abstract'

// Semantic mode is built but hidden until we add textbook + OpenAlex coverage.
// Re-enable by adding it back to this array.
const availableModes = [
  { value: 'term', label: 'Keyword', icon: 'mdi-magnify' },
  { value: 'ai', label: 'Ask', icon: 'mdi-creation' },
];
const stemmingDisabled = ref(false);
const isFocused = ref(false);
const strategyMenuOpen = ref(false);
const optionsMenuOpen = ref(false);

// Autocomplete state
const suggestions = ref([]);
const highlightedIndex = ref(-1);
const dropdownOpen = ref(false);
const isUserTyping = ref(false);
const showDropdown = computed(() => dropdownOpen.value && suggestions.value.length > 0);
const showRow2 = ref(true);
const isWorksEntity = computed(() => entityType.value === 'works');

// Entity type: read from route on Serp, store elsewhere
const entityType = computed({
  get() {
    if (route.name === 'Serp' && route.params.entityType) {
      return route.params.entityType;
    }
    return store.state.entityType;
  },
  set(to) {
    url.pushToRoute(router, {
      name: 'Serp',
      params: { entityType: to },
    });
  },
});

// Xpac toggle (route query driven)
const isXpacEnabled = computed(() => route.query.include_xpac === 'true');

function toggleXpac() {
  const newQuery = { ...route.query };
  if (isXpacEnabled.value) {
    delete newQuery.include_xpac;
  } else {
    newQuery.include_xpac = 'true';
  }
  url.pushToRoute(router, {
    name: route.name,
    params: route.params,
    query: newQuery,
  });
}

// Map searchMode + searchField + stemmingDisabled → URL search param type
const resolvedSearchType = computed(() => {
  if (!isWorksEntity.value) return 'search';
  if (searchMode.value === 'semantic') return 'search.semantic';

  const fieldMap = {
    all: stemmingDisabled.value ? 'search.exact' : 'search',
    title: stemmingDisabled.value ? 'search.title.exact' : 'search.title',
    title_and_abstract: stemmingDisabled.value ? 'search.title_and_abstract.exact' : 'search.title_and_abstract',
  };
  return fieldMap[searchField.value] || 'search';
});

const placeholder = computed(() => {
  if (!isWorksEntity.value) {
    const config = getEntityConfig(entityType.value);
    return config?.placeholder || `Search ${entityType.value}`;
  }
  if (searchMode.value === 'ai') {
    return 'Ask a question about supply chain research';
  }
  if (searchMode.value === 'semantic') {
    return 'Search vendor white papers, case studies, textbooks';
  }
  return 'Search supply chain research';
});

// When switching to a non-keyword mode, reset field/stemming to defaults
watch(searchMode, (val) => {
  if (val !== 'term') {
    searchField.value = 'title_and_abstract';
    stemmingDisabled.value = false;
  }
});

// Menu actions that auto-submit when there's a search term
function setField(value) {
  searchField.value = value;
  submitIfHasSearch();
}

function setMode(value) {
  searchMode.value = value;
  nextTick(() => {
    if (searchString.value) {
      submitSearch();
    } else if (value === 'semantic') {
      // No search term yet — still clean up incompatible filters/group_by
      cleanupForSemanticMode();
    }
  });
}

// AI mode: navigate to /ask with the question prefilled
function submitAiSearch() {
  const q = (searchString.value || '').trim();
  router.push({
    name: 'Ask',
    query: q ? { q } : {},
  });
}

// Semantic mode: navigate to /semantic with the query
function submitSemanticSearch() {
  const q = (searchString.value || '').trim();
  router.push({
    name: 'Semantic',
    query: q ? { q } : {},
  });
}

function cleanupForSemanticMode() {
  const semanticAllowedKeys = new Set(
    facetConfigs('works')
      .filter(c => c.semanticSearchAllowed && c.entityToFilter === 'works')
      .map(c => c.key)
  );

  const currentQuery = { ...route.query };
  let changed = false;

  // Strip incompatible filters
  if (currentQuery.filter) {
    const currentFilters = filtersFromUrlStr('works', currentQuery.filter);
    const compatible = currentFilters.filter(f => semanticAllowedKeys.has(f.key));
    if (compatible.length !== currentFilters.length) {
      currentQuery.filter = filtersAsUrlStr(compatible) || undefined;
      changed = true;
    }
  }

  // Clear group_by (not supported in semantic search)
  if (currentQuery.group_by) {
    delete currentQuery.group_by;
    changed = true;
  }

  if (changed) {
    url.pushToRoute(router, {
      name: route.name,
      params: route.params,
      query: currentQuery,
    });
  }
}

function disableStemming(value) {
  stemmingDisabled.value = value;
  nextTick(() => submitIfHasSearch());
}

function submitIfHasSearch() {
  if (searchString.value) {
    nextTick(() => submitSearch());
  }
}

// Autocomplete
let fetchId = 0;

function entityIcon(type) {
  return getEntityConfig(type)?.icon || 'mdi-tag-outline';
}

function countCompleteWords(str) {
  // A "complete word" is a word followed by whitespace or another word.
  // "machine learning" = 1 complete word, "machine learning " = 2, "machine learning m" = 2
  const trimmed = str.trimStart();
  if (!trimmed) return 0;
  const words = trimmed.split(/\s+/);
  // If string ends with whitespace, all words are complete
  if (/\s$/.test(str)) return words.length;
  // Otherwise the last word is incomplete, so complete = total - 1
  return Math.max(0, words.length - 1);
}

function latinize(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function authorNameMatchesQuery(displayName, query) {
  // At least one query word must appear fully in the display_name (latinized, lowercased)
  const nameNorm = latinize(displayName || '').toLowerCase();
  const queryWords = latinize(query).toLowerCase().split(/\s+/).filter(Boolean);
  return queryWords.some(word => nameNorm.includes(word));
}

function dedupeByName(items) {
  const map = new Map();
  for (const item of items) {
    const key = (item.display_name || '').toLowerCase();
    const existing = map.get(key);
    if (!existing || (item.works_count || 0) > (existing.works_count || 0)) {
      map.set(key, item);
    }
  }
  return [...map.values()];
}

function topByWorksCount(items, n) {
  return [...items].sort((a, b) => (b.works_count || 0) - (a.works_count || 0)).slice(0, n);
}

async function searchEntities(entityType, query) {
  // Use the search endpoint for full-text matching (not just prefix).
  // Fetch extra results so we can filter/re-rank client-side by works_count.
  const resp = await api.get(`/${entityType}`, {
    search: query,
    per_page: 10,
    select: 'id,display_name,works_count',
  });
  return resp.results || [];
}

// Look up a source (journal) by ISSN so an ISSN-shaped query surfaces the
// matching journal directly in the dropdown (zd#8095), the way DOI/ORCID jump
// to their entity. Returns the source result or null.
async function lookupSourceByIssn(issn) {
  try {
    const resp = await api.get('/sources', {
      filter: `issn:${issn}`,
      per_page: 1,
      select: 'id,display_name,works_count',
    });
    return (resp.results && resp.results[0]) || null;
  } catch (e) {
    return null;
  }
}

async function fetchSuggestions(query) {
  if (!query || query.length === 0) {
    suggestions.value = [];
    dropdownOpen.value = false;
    return;
  }
  const id = ++fetchId;
  const currentEntity = entityType.value;
  const config = getEntityConfig(currentEntity);

  // Kick off an ISSN→journal lookup in parallel; merged into the dropdown below.
  const issn = extractIssn(query);
  const issnSourcePromise = issn ? lookupSourceByIssn(issn) : null;

  const tag = (items, type) =>
    dedupeByName(items || []).map(item => ({
      ...item,
      _acType: type,
      _icon: entityIcon(type),
    }));

  if (currentEntity === 'works') {
    // Works SERP: suggest authors, institutions, keywords (as filters) + work titles
    const calls = [
      searchEntities('authors', query),
      searchEntities('institutions', query),
      api.getAutocomplete('keywords', { q: query }),
    ];
    const includeWorks = countCompleteWords(query) >= 3;
    if (includeWorks) {
      calls.push(api.getAutocomplete('works', { q: query }));
    }

    const settled = await Promise.allSettled(calls);
    if (id !== fetchId) return;

    let authors = settled[0].status === 'fulfilled' ? tag(settled[0].value, 'authors') : [];
    authors = authors.filter(a => authorNameMatchesQuery(a.display_name, query));
    let institutions = settled[1].status === 'fulfilled' ? tag(settled[1].value, 'institutions') : [];
    let keywords = settled[2].status === 'fulfilled' ? tag(settled[2].value, 'keywords') : [];
    let works = includeWorks && settled[3].status === 'fulfilled' ? tag(settled[3].value, 'works') : [];

    suggestions.value = topByWorksCount([...authors, ...institutions, ...keywords, ...works], 5);
  } else if (!config?.hasAutocomplete) {
    // No autocomplete for this entity type
    suggestions.value = [];
    dropdownOpen.value = false;
    return;
  } else if (config?.isNative) {
    // Native entities (authors, institutions, sources, publishers, funders, etc.)
    const results = await searchEntities(currentEntity, query);
    if (id !== fetchId) return;
    let items = tag(results, currentEntity);
    if (currentEntity === 'authors') {
      items = items.filter(a => authorNameMatchesQuery(a.display_name, query));
    }
    suggestions.value = topByWorksCount(items, 5);
  } else {
    // Non-native entities with autocomplete (keywords, topics, subfields, etc.)
    const results = await api.getAutocomplete(currentEntity, { q: query });
    if (id !== fetchId) return;
    suggestions.value = tag(results || [], currentEntity).slice(0, 5);
  }

  // If the query was an ISSN and it resolved to a journal, surface that journal
  // as the top suggestion (selecting it routes to the source page).
  if (issnSourcePromise) {
    const src = await issnSourcePromise;
    if (id !== fetchId) return;
    if (src) {
      const item = { ...src, _acType: 'sources', _icon: entityIcon('sources') };
      suggestions.value = [item, ...suggestions.value.filter(s => s.id !== item.id)].slice(0, 5);
    }
  }

  highlightedIndex.value = -1;
  dropdownOpen.value = suggestions.value.length > 0;
}

const debouncedFetch = debounce(fetchSuggestions, 200);

watch(searchString, (val) => {
  if (!isUserTyping.value) return;
  if (val) {
    debouncedFetch(val);
  } else {
    debouncedFetch.cancel();
    suggestions.value = [];
    dropdownOpen.value = false;
  }
});

function dismissDropdown() {
  dropdownOpen.value = false;
  highlightedIndex.value = -1;
}

function selectSuggestion(item) {
  isUserTyping.value = false;
  dismissDropdown();
  searchString.value = '';

  const entityId = item.id?.replace('https://openalex.org/', '') || item.id;
  router.push({ name: 'EntityPage', params: { entityType: item._acType, entityId } });
}

function onEnter() {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < suggestions.value.length) {
    selectSuggestion(suggestions.value[highlightedIndex.value]);
  } else {
    dismissDropdown();
    submitSearch();
  }
}

function onArrowDown() {
  if (!showDropdown.value) return;
  highlightedIndex.value = Math.min(highlightedIndex.value + 1, suggestions.value.length - 1);
}

function onArrowUp() {
  if (!showDropdown.value) return;
  highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
}

function onEscape() {
  if (showDropdown.value) {
    dismissDropdown();
  }
}

function onTextareaInput() {
  isUserTyping.value = true;
  resizeTextarea();
}

// A paste updates the v-model value but, unlike a keystroke, does not run our
// @input handler — so isUserTyping never flips and the suggestion watcher bails
// (zd#8095: pasting an ISSN/term fired no lookup until you pressed a key). Flag
// it as user input and kick the fetch once the pasted text has landed.
function onTextareaPaste() {
  isUserTyping.value = true;
  nextTick(() => {
    if (searchString.value) debouncedFetch(searchString.value);
    resizeTextarea();
  });
}

function resizeTextarea() {
  nextTick(() => {
    const el = inputRef.value;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  });
}

function onFocus() {
  isFocused.value = true;
  if (suggestions.value.length > 0 && searchString.value) {
    dropdownOpen.value = true;
  }
}

function onBlur() {
  isFocused.value = false;
  dismissDropdown();
}

onBeforeUnmount(() => {
  debouncedFetch.cancel();
});

// Populate from URL on mount and route changes
function syncFromRoute() {
  isUserTyping.value = false;
  const searchFromRoute = url.getSearchFromRoute(route);
  if (searchFromRoute) {
    searchString.value = searchFromRoute.value;
    const type = searchFromRoute.type;

    if (type === 'search.semantic') {
      searchMode.value = 'semantic';
      searchField.value = 'title_and_abstract';
      stemmingDisabled.value = false;
    } else if (type === 'search.title.exact') {
      searchMode.value = 'term';
      searchField.value = 'title';
      stemmingDisabled.value = true;
    } else if (type === 'search.title') {
      searchMode.value = 'term';
      searchField.value = 'title';
      stemmingDisabled.value = false;
    } else if (type === 'search.title_and_abstract.exact') {
      searchMode.value = 'term';
      searchField.value = 'title_and_abstract';
      stemmingDisabled.value = true;
    } else if (type === 'search.title_and_abstract') {
      searchMode.value = 'term';
      searchField.value = 'title_and_abstract';
      stemmingDisabled.value = false;
    } else if (type === 'search.exact') {
      searchMode.value = 'term';
      searchField.value = 'all';
      stemmingDisabled.value = true;
    } else {
      // 'search' (default)
      searchMode.value = 'term';
      searchField.value = 'all';
      stemmingDisabled.value = false;
    }
  } else {
    searchString.value = '';
  }
}

onMounted(() => {
  syncFromRoute();
  resizeTextarea();
});
watch(() => route.fullPath, () => {
  dismissDropdown();
  syncFromRoute();
  resizeTextarea();
});

async function submitSearch(forceEntityType) {
  if (!searchString.value && !props.showExamples) return;

  const targetEntityType = forceEntityType || entityType.value;

  if (!searchString.value) {
    url.pushToRoute(router, { name: 'Serp', params: { entityType: targetEntityType } });
    return;
  }

  // AI mode: route to chat
  if (searchMode.value === 'ai') {
    submitAiSearch();
    return;
  }

  // Semantic mode: route to our content store search results page
  if (searchMode.value === 'semantic') {
    submitSemanticSearch();
    return;
  }

  // Intercept DOI/ORCID before regular search
  const handled = await tryIdentifierLookup();
  if (handled) return;

  // (Legacy: OpenAlex semantic search via URL filter - we keep the code path
  // dormant in case Phase 4 enables OpenAlex semantic embeddings.)
  if (searchMode.value === 'openalex_semantic') {
    const semanticAllowedKeys = new Set(
      facetConfigs('works')
        .filter(c => c.semanticSearchAllowed && c.entityToFilter === 'works')
        .map(c => c.key)
    );
    const currentQuery = { ...route.query };
    // Remove all search params
    ['search', 'search.exact', 'search.semantic', 'search.title', 'search.title.exact',
     'search.title_and_abstract', 'search.title_and_abstract.exact'].forEach(k => delete currentQuery[k]);
    currentQuery['search.semantic'] = searchString.value;
    currentQuery.page = 1;
    currentQuery.sort = 'relevance_score:desc';
    delete currentQuery.group_by;
    // Strip incompatible filters
    if (currentQuery.filter) {
      const currentFilters = filtersFromUrlStr('works', currentQuery.filter);
      const compatible = currentFilters.filter(f => semanticAllowedKeys.has(f.key));
      currentQuery.filter = filtersAsUrlStr(compatible) || undefined;
    }
    url.pushToRoute(router, {
      name: 'Serp',
      params: { entityType: targetEntityType },
      query: currentQuery,
    });
    return;
  }

  url.setNewSearch(targetEntityType, resolvedSearchType.value, searchString.value);
}

function clearSearch() {
  searchString.value = '';
  url.clearNewSearch();
  inputRef.value?.focus();
}

// Vuetify's v-menu moves focus (to its activator, then <body>) while it
// closes, which clobbers a single focus() call. Retry over a short window
// until focus actually sticks on the input.
function focusSearchInput() {
  const el = inputRef.value;
  if (!el) return;
  const deadline = Date.now() + 500;
  const tryFocus = () => {
    if (document.activeElement === el) return;
    el.focus();
    if (document.activeElement !== el && Date.now() < deadline) {
      setTimeout(tryFocus, 50);
    }
  };
  nextTick(tryFocus);
}
</script>

<style lang="scss" scoped>
.search-box {
  position: relative;
  border: 1px solid #E0E0E0;
  border-radius: 16px;
  background: white;
  transition: border-color 0.15s ease;

  &--focused {
    border-color: #B8AFFE;
    box-shadow: 0 0 0 3px rgba(210, 203, 254, 0.5);
  }
}

.search-row-1 {
  min-height: 52px;
}

.search-divider {
  height: 1px;
  background-color: #F0F0F0;
  margin: 0 16px;
}

.search-row-2 {
  min-height: 40px;
  padding: 4px 8px;
}

.search-input {
  border: none;
  outline: none;
  font-size: 16px;
  line-height: 1.5;
  padding: 14px 18px;
  background: transparent;
  font-family: Inter, sans-serif;
  min-width: 0;
  resize: none;
  max-height: 480px; /* ~20 lines */
  overflow-y: auto;

  &::placeholder {
    color: #9CA3AF;
  }
}

.control-btn {
  color: #6B7280 !important;

  &:hover {
    color: #374151 !important;
  }
}

.control-label {
  font-size: 13px;
  font-weight: 500;
}

// Mode toggle (Keyword / Ask / Semantic)
.mode-toggle {
  display: inline-flex;
  align-items: center;
  background: #f4f4f5;
  border-radius: 999px;
  padding: 2px;
  gap: 0;
}

.mode-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: none;
  background: transparent;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover:not(&--active) {
    color: #041708;
  }
}

.mode-toggle-btn--active {
  background: #041708;
  color: #ffffff;

  .mode-toggle-icon {
    color: #cdfc8a !important;
  }
}

.mode-toggle-icon {
  color: inherit !important;
}

.mode-toggle-label {
  letter-spacing: 0.3px;
}

.mode-hint {
  font-size: 12px;
  color: #818982;
  margin-right: 12px;
  font-style: italic;
}

@media (max-width: 600px) {
  .mode-toggle-label {
    display: none;
  }
  .mode-hint {
    display: none;
  }
}

.control-btn--disabled {
  opacity: 0.5 !important;
  cursor: default !important;
}

.menu-subtitle {
  font-size: 12px !important;
  color: #4B5563 !important;
}

.check-icon {
  color: #1a1a1a !important;
  font-weight: 900;
}

.autocomplete-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
}

.autocomplete-list {
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
}

.autocomplete-item {
  padding: 8px 18px;
  cursor: pointer;
  transition: background-color 0.1s ease;

  &--highlighted {
    background-color: #F3F4F6;
  }
}

.autocomplete-item-icon {
  color: #9CA3AF;
  flex-shrink: 0;
  margin-right: 10px;
}

.autocomplete-item-name {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.4;
}

</style>
